/**
 * Field Marks API Service
 */
import apiClient from './apiClient';
import { authService } from './authService';

export interface FieldMark {
  id: number;
  farm_id: string;
  field_id: string | null;
  sector_id: number | null;
  latitude: number;
  longitude: number;
  mark_type: string;
  title: string;
  description: string | null;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'resolved' | 'cancelled' | 'archived';
  assigned_to: number | null;
  assigned_at: string | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  attachment_count: number;
}

export interface FieldMarkCreate {
  latitude: number;
  longitude: number;
  mark_type: string;
  title: string;
  description?: string;
  priority?: string;
}

export interface FieldMarkAttachmentMeta {
  id: number;
  field_mark_id: number;
  attachment_type: 'photo' | 'audio';
  filename: string;
  mime_type: string;
  file_size_bytes: number;
  has_thumbnail: boolean;
  created_at: string;
}

export const MARK_TYPES = [
  'water_leak',
  'blocked_dripper',
  'broken_pipe',
  'pest',
  'equipment',
  'soil_issue',
  'crop_damage',
  'phenology',
  'other',
] as const;

export const PRIORITY_LEVELS = ['low', 'medium', 'high', 'urgent'] as const;

const fieldMarkService = {
  list: async (farmId: string, filters?: Record<string, string>): Promise<FieldMark[]> => {
    const params = new URLSearchParams(filters || {});
    return apiClient.get<FieldMark[]>(`/farms/${farmId}/field-marks?${params}`);
  },

  get: async (farmId: string, markId: number): Promise<FieldMark> => {
    return apiClient.get<FieldMark>(`/farms/${farmId}/field-marks/${markId}`);
  },

  create: async (farmId: string, data: FieldMarkCreate): Promise<FieldMark> => {
    return apiClient.post<FieldMark>(`/farms/${farmId}/field-marks`, data);
  },

  update: async (farmId: string, markId: number, data: Partial<FieldMarkCreate & { status: string }>): Promise<FieldMark> => {
    return apiClient.put<FieldMark>(`/farms/${farmId}/field-marks/${markId}`, data);
  },

  delete: async (farmId: string, markId: number): Promise<void> => {
    await apiClient.delete(`/farms/${farmId}/field-marks/${markId}`);
  },

  assign: async (farmId: string, markId: number, assignedTo: number): Promise<FieldMark> => {
    return apiClient.post<FieldMark>(`/farms/${farmId}/field-marks/${markId}/assign`, { assigned_to: assignedTo });
  },

  listAttachments: async (farmId: string, markId: number): Promise<FieldMarkAttachmentMeta[]> => {
    return apiClient.get<FieldMarkAttachmentMeta[]>(`/farms/${farmId}/field-marks/${markId}/attachments`);
  },

  resolve: async (farmId: string, markId: number, notes?: string): Promise<FieldMark> => {
    return apiClient.post<FieldMark>(`/farms/${farmId}/field-marks/${markId}/resolve`, { resolution_notes: notes });
  },

  archive: async (farmId: string, markId: number): Promise<FieldMark> => {
    return apiClient.put<FieldMark>(`/farms/${farmId}/field-marks/${markId}`, { status: 'archived' });
  },

  uploadAttachment: async (farmId: string, markId: number, file: File, type: 'photo' | 'audio'): Promise<FieldMarkAttachmentMeta> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('attachment_type', type);
    const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
    const authHeaders = authService.getAuthHeaders();
    const response = await fetch(`${API_BASE}/farms/${farmId}/field-marks/${markId}/attachments`, {
      method: 'POST',
      headers: { ...authHeaders },
      body: formData,
      credentials: 'include',
    });
    return response.json();
  },

  getAttachmentUrl: (farmId: string, markId: number, attachmentId: number, thumbnail = false): string => {
    const base = `${import.meta.env.VITE_API_URL}/farms/${farmId}/field-marks/${markId}/attachments/${attachmentId}`;
    return thumbnail ? `${base}?thumbnail=true` : base;
  },

  deleteAttachment: async (farmId: string, markId: number, attachmentId: number): Promise<void> => {
    await apiClient.delete(`/farms/${farmId}/field-marks/${markId}/attachments/${attachmentId}`);
  },
};

export default fieldMarkService;
